<template>
    <form class="form">
        <div class="form__row row">
            <label for="name" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.name') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="name" name="name" :placeholder="$t('fields.name')" v-model.trim="$v.user.name.$model" :disabled="ssoUser" />

                <div v-if="$v.user.name.$error">
                    <p class="form__error">{{ $t('fields.name_e_required') }}</p>
                </div>
            </div>
        </div>

        <div class="form__row row">
            <label for="email" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.email') }}</label>

            <div class="column-mobile-16">
                <input
                    type="email"
                    class="form__field"
                    id="email"
                    name="email"
                    :placeholder="$t('fields.email')"
                    v-model.trim="$v.user.email.$model"
                    @input="emptyRemoteError"
                    :disabled="!initialEmpty || ssoUser"
                />

                <div v-if="$v.user.email.$error || remoteErrorMessage.length > 0">
                    <p class="form__error" v-show="!$v.user.email.required">
                        {{ $t('fields.email_e_required') }}
                    </p>
                    <p class="form__error" v-show="!$v.user.email.email">
                        {{ $t('fields.email_e_email') }}
                    </p>
                    <p class="form__error" v-show="!$v.user.email.emailRegex">
                        {{ $t('fields.email_e_windhager') }}
                    </p>
                    <p class="form__error">{{ remoteErrorMessage }}</p>
                </div>
            </div>
        </div>

        <div class="form__row row" v-if="showPassword">
            <label for="password" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.password') }}</label>

            <div class="column-mobile-16">
                <input type="password" class="form__field" id="password" name="password" :placeholder="$t('fields.password')" v-model.trim="$v.user.password.$model" :disabled="ssoUser" />
            </div>
        </div>

        <div class="form__row row" v-if="showPassword">
            <label for="passwordRepeat" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.passwordRepeat') }}</label>

            <div class="column-mobile-16">
                <input
                    type="password"
                    class="form__field"
                    id="passwordRepeat"
                    name="passwordRepeat"
                    :placeholder="$t('fields.passwordRepeat')"
                    v-model.trim="$v.user.passwordRepeat.$model"
                    :disabled="ssoUser"
                />
            </div>
        </div>

        <div class="form__row row" v-if="showPassword">
            <label class="form__label column-mobile-8 hide-until-mobile-landscape">&nbsp;</label>

            <div class="form__info">
                <p class="form__error" :class="{ 'form__error--success': $v.user.password.minLength && user.password.length > 0 }">
                    •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.minlength') }}</span>
                </p>
                <p class="form__error" :class="{ 'form__error--success': $v.user.password.bigSmall }">
                    •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.bigsmall') }}</span>
                </p>
                <p class="form__error" :class="{ 'form__error--success': $v.user.password.nr }">
                    •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.number') }}</span>
                </p>
                <p class="form__error" :class="{ 'form__error--success': $v.user.password.extra }">
                    •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.extra') }}</span>
                </p>
                <p class="form__error" :class="{ 'form__error--success': $v.user.password.sameAsPassword && user.password.length > 0 }">
                    •&nbsp;&nbsp;<span>{{ $t('useraccount.passworderror.samepassword') }}</span>
                </p>
            </div>
        </div>

        <div class="form__row row">
            <label for="address1" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.address1') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="address1" name="address1" :placeholder="$t('fields.address1_help')" v-model="user.address1" :disabled="ssoUser" />
                <!-- <div class="form__help">{{ $t('fields.address1_help') }}</div> -->
            </div>
        </div>

        <div class="form__row row">
            <label for="address2" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.address2') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="address2" name="address2" :placeholder="$t('fields.address2_help')" v-model="user.address2" :disabled="ssoUser" />
                <!-- <div class="form__help">{{ $t('fields.address2_help') }}</div> -->
            </div>
        </div>

        <div class="form__row row">
            <label for="postcode" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.postcode') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="postcode" name="postcode" :placeholder="$t('fields.postcode')" v-model="user.postcode" :disabled="ssoUser" />
            </div>
        </div>

        <div class="form__row row">
            <label for="city" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.city') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="city" name="city" :placeholder="$t('fields.city')" v-model="user.city" :disabled="ssoUser" />
            </div>
        </div>

        <div class="form__row row">
            <label for="region" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.region') }}</label>

            <div class="column-mobile-16">
                <input type="text" class="form__field" id="region" name="region" :placeholder="$t('fields.region_help')" v-model="user.region" :disabled="ssoUser" />
                <!-- <div class="form__help">{{ $t('fields.region_help') }}</div> -->
            </div>
        </div>

        <div class="form__row row">
            <label for="country" class="form__label column-mobile-8" :class="{ disabled: ssoUser }">{{ $t('fields.country') }}</label>

            <div class="column-mobile-16 appearance">
                <country-select class="form__field" id="country" name="country" :preselectedValue="user.country" @input="updateUser('country', $event)" :disabled="ssoUser"></country-select>
            </div>
        </div>

        <div class="form__row row">
            <label for="locale" class="form__label column-mobile-8">{{ $t('fields.language') }}</label>

            <div class="column-mobile-16 appearance">
                <language-select ref="languageSelect" id="locale" name="locale" :preselectedValue="user.locale" @input="updateUser('locale', $event)" :disabled="user.id === ''"></language-select>
            </div>
        </div>

        <div class="row" v-if="showTerms">
            <div class="column-mobile-16 column--pushright brick-mobile--tiny--top brick-mobile--tiny--bottom">
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="accept_terms" v-model.trim="$v.acceptTerms.$model" />
                        <span v-html="$t('registration.accept_terms', { country: user.country })" ref="acceptTermsLabel"></span>
                    </label>
                    <div v-if="$v.acceptTerms.$error">
                        <p class="help-block form__error">
                            {{ $t('registration.accept_terms_e_required') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { required, email, minLength, maxLength, helpers, sameAs } from 'vuelidate/lib/validators';
import CountrySelect from '@/components/CountrySelect';
import LanguageSelect from '@/components/LanguageSelect';
import openTermsMixin from '@/mixins/openTermsMixin';
import * as api from '@/api';

const nr = value => /[0-9]/.test(value);
const bigSmall = value => /[a-z]/.test(value) && /[A-Z]/.test(value);
const extra = value => /[^0-9a-zA-Z ]/.test(value);

export default {
    components: {
        CountrySelect,
        LanguageSelect,
    },
    mixins: [openTermsMixin],
    props: {
        showTerms: {
            type: Boolean,
            default: false,
        },
        user: {
            type: Object,
            default() {
                return {
                    name: '',
                    email: '',
                    password: '',
                    passwordRepeat: '',
                    address1: '',
                    address2: '',
                    postcode: '',
                    city: '',
                    region: '',
                    country: '',
                    locale: '',
                };
            },
        },
        showPassword: {
            type: Boolean,
            default: false,
        },
        initialEmpty: {
            type: Boolean,
            default: true,
        },
        ssoUser: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            acceptTerms: false,
            remoteErrorMessage: '',
            latestTerms: false,
        };
    },
    computed: {
        ...mapState(['termsOfUseVersion']),
        ...mapGetters(['preferredLanguage']),
    },
    watch: {
        'user.locale': {
            handler() {
                this.$refs.languageSelect.updatePreselectedValue(this.user.locale);
            },
        },
    },
    validations() {
        const emailRegex = helpers.regex('emailRegex', /^((?!@windhager\.com).)*$/);

        let validations = {
            user: {
                name: {
                    required,
                },
                email: {
                    required,
                    email,
                },
            },
            errorMessage: false,
        };

        if (this.showPassword) {
            validations.user.password = {
                required,
                minLength: minLength(12),
                maxLength: maxLength(128),
                sameAsPassword: sameAs(function() {
                    return this.user.passwordRepeat;
                }),
                nr,
                bigSmall,
                extra,
            };
            validations.user.passwordRepeat = {
                required,
            };
        }

        if (this.showTerms) {
            validations.acceptTerms = {
                required(val) {
                    return val;
                },
            };
        }

        if (this.initialEmpty) {
            validations.user.email = {
                required,
                email,
                emailRegex,
            };
        }
        return validations;
    },
    mounted() {
        this.initForm();
    },
    methods: {
        async initForm() {
            let that = this;
            if (this.showTerms) {
                await this.loadLatestTermsVersion();
                this.$refs['acceptTermsLabel'].getElementsByTagName('a')[0].addEventListener('click', function(e) {
                    e.preventDefault();
                    that.openTerms(false);
                });

                // if new user, save terms version too
                this.user.accepted_data_policy_version = this.latestTerms.version;
            }

            // set initial language from preferred language in store
            // usually the one from the browser except user changed it
            if (this.initialEmpty && this.user.locale === '') {
                this.user.locale = this.preferredLanguage;
                this.$refs.languageSelect.updatePreselectedValue(this.preferredLanguage);
            }
        },
        async loadLatestTermsVersion() {
            try {
                const latestTerms = await api.users.getLatestTerms();
                this.latestTerms = latestTerms.data;
            } catch (error) {
                console.error(error);
            }
        },
        updateUser(field, value) {
            this.user[field] = value;
        },
        emptyRemoteError() {
            this.remoteErrorMessage = '';
        },
    },
};
</script>
